<template>
  <component
    :is="as"
    ref="buttonRef"
    :class="buttonClasses"
    :disabled="disabled"
    v-bind="$attrs"
  >
    <span
      ref="textRef"
      class="relative z-20 flex items-center justify-center w-full"
      :class="{
        'transition-opacity duration-200 hover:opacity-80':
          disableTextAnimation && !disabled,
      }"
    >
      <slot />
      <slot name="icon" />
      <ElementsIconsArrow v-if="icon" />
    </span>
  </component>
</template>

<script setup lang="ts">
import type { Component } from 'vue'
import { useEventListener } from '@vueuse/core'
import gsap from 'gsap'

interface Props {
  as?: string | Component
  outlined?: boolean
  variant?: 'primary' | 'secondary'
  size?: 'regular' | 'small'
  color?: 'accent' | 'dark' | 'light'
  disabled?: boolean
  icon?: boolean
  fullWidth?: boolean
  disableTextAnimation?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  as: 'button',
  variant: 'primary',
  color: 'accent',
  size: 'regular',
  disableTextAnimation: false,
})

const buttonRef = ref<HTMLElement>()
const textRef = ref<HTMLElement>()

const STYLES = {
  base: 'relative text-base font-normal overflow-hidden flex items-center justify-center transition-all duration-75 ease-linear cursor-pointer disabled:cursor-not-allowed focus-visible:ring-2 focus-visible:ring-offset-2',

  disabled: 'bg-[#807B7A14] text-[#1616162E]',

  colors: {
    accent: {
      filled: 'bg-accent text-crosswalk',
      outlined: 'border-accent text-crosswalk',
    },
    dark: {
      filled: 'bg-action-primary text-crosswalk',
      outlined: 'border-action-primary text-action-primary',
    },
    light: {
      filled: 'bg-tertiary/18 text-black',
      outlined: 'border-tertiary/18 text-black',
    },
  },
  variants: {
    primary: 'rounded-[10px]',
    secondary: 'rounded-full',
  },
  sizes: {
    regular: 'h-12 px-6 4xl:px-8 5xl:px-10',
    small: 'py-2 px-4',
  },
} as const

const buttonClasses = computed(() => {
  const classes: string[] = [STYLES.base]

  if (props.disabled) {
    classes.push(STYLES.disabled)
  } else {
    const colorStyle = STYLES.colors[props.color]
    classes.push(props.outlined ? colorStyle.outlined : colorStyle.filled)

    if (props.outlined) {
      classes.push('border')
    }
  }

  classes.push(STYLES.sizes[props.size])
  classes.push(STYLES.variants[props.variant])
  classes.push(props.fullWidth ? 'w-full' : 'min-w-fit')

  if (props.disableTextAnimation && !props.disabled && !props.icon) {
    classes.push('transition-opacity duration-75 hover:opacity-60 ease-in-out')
  }

  return classes
})

const setupArrowAnimation = () => {
  const arrowLine = buttonRef.value?.querySelector(
    '.arrow-line'
  ) as SVGRectElement
  if (!arrowLine) return

  const animateArrow = (expanded: boolean) => {
    if (props.disabled) return
    gsap.to(arrowLine, {
      duration: 0.3,
      attr: expanded ? { x: 9, width: 20 } : { x: 20, width: 9 },
      ease: 'power2.out',
    })
  }

  useEventListener(buttonRef, 'mouseenter', () => animateArrow(true))
  useEventListener(buttonRef, 'mouseleave', () => animateArrow(false))
}

const setupTextAnimation = () => {
  if (props.icon || props.disableTextAnimation) return

  const timeline = gsap
    .timeline({ paused: true })
    .to(textRef.value!, { duration: 0.15, yPercent: -150, ease: 'power2.in' })
    .set(textRef.value!, { yPercent: 150 })
    .to(textRef.value!, { duration: 0.15, yPercent: 0, ease: 'power2.out' })

  useEventListener(buttonRef, 'mouseenter', () => {
    if (!props.disabled) timeline.play(0)
  })
}

const setupAnimations = () => {
  if (!buttonRef.value || !textRef.value) return

  if (props.icon) {
    setupArrowAnimation()
  } else if (!props.disableTextAnimation) {
    setupTextAnimation()
  }
}

onMounted(setupAnimations)
</script>

<style scoped>
.arrow-svg {
  width: auto;
  min-width: 32px;
  overflow: visible;
  flex-shrink: 0;
}
</style>
